import { useEffect, useRef } from "react";
import { useAtomValue } from "jotai";
import {
  tableAtomFamily_SUSPENDS,
  fetchChatbotAtomFamily_SUSPENDS,
  fetchAgentAtomFamily_SUSPENDS,
  fetchInterfacePageAtomFamily_SUSPENDS,
  determineAssetType,
  getAssetId,
} from "@zapier/canvas-state";
import useUpdateNode from "./useUpdateNode";

type AssetTitle = {
  table?: string;
  chatbot?: string;
  agent?: string;
  interface?: string;
  zap?: string;
};

type UseAssetTitleSyncOptions = {
  nodeData: any;
  nodeType: string;
  label?: string;
  zapTitle?: string;
};

/**
 * Hook that synchronizes asset titles (Table, Interface, Chatbot, Agent, Zap) with step titles.
 * If there's no step title but an asset has a title/name, it sets the asset title as the step title.
 * This only happens once per node to avoid overwriting user-entered titles.
 */
export function useAssetTitleSync({
  nodeData,
  nodeType,
  label,
  zapTitle,
}: UseAssetTitleSyncOptions) {
  const hasSetAssetTitleAsStepTitle = useRef(false);
  const updateNodeAndSave = useUpdateNode();

  const assetType = determineAssetType(nodeData?.services, nodeType as any);
  const assetId = getAssetId(nodeData, assetType);

  // Get asset data based on asset type
  const tableData = useAtomValue(
    tableAtomFamily_SUSPENDS(assetType === "table" ? assetId : undefined),
  );
  const chatbotResult = useAtomValue(
    fetchChatbotAtomFamily_SUSPENDS(
      assetType === "chatbot" ? assetId : undefined,
    ),
  );
  const agentResult = useAtomValue(
    fetchAgentAtomFamily_SUSPENDS(assetType === "agent" ? assetId : undefined),
  );
  const interfaceResult = useAtomValue(
    fetchInterfacePageAtomFamily_SUSPENDS(
      assetType === "interface"
        ? {
            interfacePageId: assetId,
            interfaceProjectId: nodeData?.projectId,
          }
        : { interfacePageId: undefined, interfaceProjectId: undefined },
    ),
  );

  // Extract titles from various asset sources
  const assetTitles: AssetTitle = {
    table: tableData?.type === "success" ? tableData.data?.name : undefined,
    chatbot:
      chatbotResult?.type === "success" ? chatbotResult.data?.name : undefined,
    agent: agentResult?.type === "success" ? agentResult.data?.name : undefined,
    interface:
      interfaceResult?.type === "success"
        ? interfaceResult.data?.name
        : undefined,
    zap: zapTitle,
  };

  // Get the relevant title for the current asset type
  const rawAssetTitle = assetTitles[assetType as keyof AssetTitle];

  // Clean up the asset title by removing "(created by Canvas)" suffix and preceding space
  const assetTitle = rawAssetTitle
    ? rawAssetTitle.replace(/ \(created by Canvas\)$/, "")
    : rawAssetTitle;

  // Reasons why we are using useEffect: https://zapier.slack.com/archives/C07KUG0V89K/p1762978053917559?thread_ts=1762978021.980559&cid=C07KUG0V89K
  useEffect(() => {
    // If there was no step title, set the asset title as the step title
    if (!hasSetAssetTitleAsStepTitle.current && !label && assetTitle) {
      updateNodeAndSave({ data: { label: assetTitle } }, { shouldSave: true });
      /* Mark hasSetAssetTitleAsStepTitle as true to prevent this logic from running again.
       * We only want this to happen on page load, not when user empties the input manually */
      hasSetAssetTitleAsStepTitle.current = true;
    }
  }, [label, assetTitle, updateNodeAndSave]);
}
